import ClearIcon from '@mui/icons-material/Clear';
import { Button, Stack } from '@mui/material';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useCatalogOptions } from '../catalog/api';
import { useProductPackageQuantities, useProductSizes } from './api';
import type { ProductFilters } from './api';

export interface ProductFilterBarProps {
  value: ProductFilters;
  onChange: (next: ProductFilters) => void;
  /** Include the series dropdown (hidden on the compact rates screen by default). */
  showSeries?: boolean;
  /** Include product activation status. */
  showStatus?: boolean;
  /** Include pieces per box (package quantity). */
  showPackageQuantity?: boolean;
}

/** Brand / category / size (and optionally series) filters shared by the product screens. */
export function ProductFilterBar({
  value,
  onChange,
  showSeries = false,
  showStatus = false,
  showPackageQuantity = false,
}: ProductFilterBarProps): JSX.Element {
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', value.brandId);
  const allSeries = useCatalogOptions(showSeries ? '/series' : null);
  const sizes = useProductSizes();
  const packageQuantities = useProductPackageQuantities();

  const brandSeries = (allSeries.data ?? []).filter(
    (s) => !value.brandId || s.parentId === value.brandId,
  );

  const hasFilters = Boolean(
    value.brandId ||
      value.categoryId ||
      value.sizeMm ||
      value.seriesId ||
      value.isActive !== undefined ||
      value.piecesPerBox !== undefined,
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <SearchableSelect
        label="Brand"
        value={value.brandId ?? ''}
        allLabel="All brands"
        options={(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }))}
        onChange={(brandId) => onChange({
          ...value,
          brandId: brandId || undefined,
          categoryId: undefined,
          seriesId: undefined,
        })}
        width={150}
      />

      <SearchableSelect
        label="Category"
        value={value.categoryId ?? ''}
        allLabel="All categories"
        options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
        onChange={(categoryId) => onChange({ ...value, categoryId: categoryId || undefined })}
        width={150}
      />

      {showSeries && (
        <SearchableSelect
          label="Series"
          value={value.seriesId ?? ''}
          allLabel="All series"
          options={brandSeries.map((series) => ({ value: series.id, label: series.name }))}
          onChange={(seriesId) => onChange({ ...value, seriesId: seriesId || undefined })}
          width={150}
        />
      )}

      <SearchableSelect
        label="Size"
        value={value.sizeMm ?? ''}
        allLabel="All sizes"
        options={(sizes.data ?? []).map((size) => ({ value: size, label: size }))}
        onChange={(sizeMm) => onChange({ ...value, sizeMm: sizeMm || undefined })}
        width={130}
      />

      {showPackageQuantity && (
        <SearchableSelect
          label="Package qty"
          value={value.piecesPerBox === undefined ? '' : String(value.piecesPerBox)}
          allLabel="All package qty"
          options={(packageQuantities.data ?? []).map((quantity) => ({
            value: String(quantity),
            label: String(quantity),
          }))}
          onChange={(quantity) => onChange({
            ...value,
            piecesPerBox: quantity === '' ? undefined : Number(quantity),
          })}
          width={150}
        />
      )}

      {showStatus && (
        <SearchableSelect
          label="Status"
          value={value.isActive === undefined ? '' : String(value.isActive)}
          allLabel="All statuses"
          options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
          onChange={(status) =>
            onChange({
              ...value,
              isActive: status === '' ? undefined : status === 'true',
            })
          }
          width={130}
        />
      )}

      {hasFilters && (
        <Button size="small" color="inherit" startIcon={<ClearIcon />} onClick={() => onChange({})}>
          Clear
        </Button>
      )}
    </Stack>
  );
}
